import { useState, type ChangeEvent } from 'react';
import { Button, ButtonToggle, InputPlain } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { t } from 'i18next';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import type { OccupationType } from '../types';

const JOB_OPTIONS: { label: string; occupationType: OccupationType }[] = [
  { label: t('SceneSetting.enterArtistInfo.singer'), occupationType: 'SINGER' },
  { label: t('SceneSetting.enterArtistInfo.actor'), occupationType: 'ACTOR' },
  { label: t('SceneSetting.enterArtistInfo.entertainer'), occupationType: 'ENTERTAINER' },
  { label: t('SceneSetting.enterArtistInfo.character'), occupationType: 'CHARACTER' },
  { label: t('SceneSetting.enterArtistInfo.etc'), occupationType: 'ETC' }
];

export type EnterArtistInfoSubmitPayload = {
  occupationType?: OccupationType;
  occupationDescription?: string;
};

interface EnterArtistInfoStepProps {
  nickname: string;
  initialOccupationType?: OccupationType | '';
  initialOccupationDescription?: string;
  onBack: () => void;
  onSubmit: (partial: EnterArtistInfoSubmitPayload) => void;
}

const EnterArtistInfoStep = ({
  nickname,
  initialOccupationType = '' as const,
  initialOccupationDescription = '',
  onBack,
  onSubmit
}: EnterArtistInfoStepProps) => {
  const [selectedJob, setSelectedJob] = useState<string>(initialOccupationType);
  const [groupName, setGroupName] = useState(initialOccupationDescription);
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const handleJobSelect = (job: string) => {
    setSelectedJob((prev) => (prev === job ? '' : job));
  };

  const getPlaceholder = (occupationType: OccupationType) => {
    switch (occupationType) {
      case 'SINGER':
        return t('SceneSetting.enterArtistInfo.singerPlaceholder');
      case 'ACTOR':
        return t('SceneSetting.enterArtistInfo.actorPlaceholder');
      case 'ENTERTAINER':
        return t('SceneSetting.enterArtistInfo.entertainerPlaceholder');
      case 'CHARACTER':
        return t('SceneSetting.enterArtistInfo.characterPlaceholder');
      case 'ETC':
        return t('SceneSetting.enterArtistInfo.etcPlaceholder');
    }
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] min-h-screen flex flex-col bg-background-dark">
      <header className="px-[16px] py-[15px]">
        <button type="button" onClick={onBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto pb-[120px]">
        <div className="flex flex-col items-start gap-[14px] px-[24px] py-[10px]">
          <h1 className="text-[26px] font-bold text-pure-white leading-[31px] whitespace-pre-line">
            {t('SceneSetting.enterArtistInfo.title', { nickname })}
          </h1>
          <p className="text-[15px] font-normal text-main-light-4 leading-[18px]">
            {t('SceneSetting.enterArtistInfo.description')}
          </p>
        </div>

        {/* Job Section */}
        <div className="mt-[20px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">
            {t('SceneSetting.enterArtistInfo.jobQuestion')}
          </p>
          <div className="flex flex-col">
            {JOB_OPTIONS.map(({ label, occupationType }) => (
              <div key={label} className="flex flex-col gap-[10px] py-[18px]">
                <button
                  type="button"
                  className="flex items-center gap-[10px] text-left"
                  onClick={() => {
                    handleJobSelect(occupationType);
                    setGroupName('');
                  }}
                >
                  <ButtonToggle
                    size="lg"
                    type="check"
                    checked={selectedJob === occupationType}
                    className="text-sub-dark-2 [--color-text-sub-lv3:currentColor] [--color-surface-lv2:var(--color-white-opacity-50)]"
                  />
                  <span className="text-[15px] font-semibold text-main-dark-2 leading-[18px]">
                    {label}
                  </span>
                </button>

                {selectedJob === occupationType && (
                  <div className="pl-[34px]">
                    <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
                      <InputPlain
                        placeholder={getPlaceholder(occupationType)}
                        className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
                        value={groupName}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setGroupName(e.target.value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-[40px] flex justify-center flex-col items-center mx-[33px] text-center">
          <p className="text-[13px] font-semibold text-main-light-4">
            {t('SceneSetting.enterArtistInfo.instruction')}
          </p>
        </div>
      </div>

      {/* Footer Button */}
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] w-full pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))] bg-background-dark transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold disabled:bg-[#303030] disabled:text-[#4D4D50]"
          disabled={!selectedJob}
          onClick={() => {
            const option = JOB_OPTIONS.find((o) => o.occupationType === selectedJob);
            const payload: EnterArtistInfoSubmitPayload = {};
            if (option) {
              payload.occupationType = option.occupationType;
              const desc = groupName.trim();
              if (desc) payload.occupationDescription = desc;
            }
            onSubmit(payload);
          }}
        >
          {t('SceneSetting.enterArtistInfo.startButton')}
        </Button>
      </div>
    </div>
  );
};

export default EnterArtistInfoStep;
